import { useState, useEffect, useRef, Fragment } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Highlighter, StickyNote, BookOpen, Languages, X, Sparkles } from 'lucide-react'
import DemoCursor from './DemoCursor'

// Bible Reader demo — looped scripted walkthrough of the chapter view,
// highlight picker, note editor, AI Insight panel, and Original Language
// accordion. Visual fidelity mirrors the real components in
// src/components/bible/* so the demo reads as a slice of the actual page,
// not a separate illustration.
//
// Four scenes, cumulative state — visitors see actions build on top of
// each other through the cycle, then everything resets and loops.
//
//   Scene 1 — highlight: tap v1 → gold; tap v4 → blue
//   Scene 2 — note:      tap v3 → type a note → save (sticky icon stays)
//   Scene 3 — insight:   tap v5 → panel slides in → markdown streams
//   Scene 4 — language:  tap v1 → insight streams → Languages accordion
//
// Verse-span hygiene: each verse <span> contains ONLY <sup>num</sup> +
// text. The space between verses lives OUTSIDE the span, so the
// selection / highlight background can't bleed backward into the
// preceding word.

const VERSES = [
  {
    num: 1,
    text: 'In the beginning was the Word, and the Word was with God, and the Word was God.',
  },
  { num: 2, text: 'He was with God in the beginning.' },
  {
    num: 3,
    text: 'Through him all things were made; without him nothing was made that has been made.',
  },
  { num: 4, text: 'In him was life, and that life was the light of all mankind.' },
  {
    num: 5,
    text: 'The light shines in the darkness, and the darkness has not overcome it.',
  },
]

// Matches HIGHLIGHT_COLORS in src/data/bibleBooks.js.
const HC = {
  gold: '#e9c349',
  blue: '#4a8fd4',
  green: '#5a9e6f',
  pink: '#d4748a',
  purple: '#9b59d6',
}

const NOTE_TEXT = 'The Word — eternal, divine, present before creation itself.'

// V5 insight — SHORT. Single tight thought, ~125 words target in the
// real LengthPills spec; here we keep it to ~25 words so the demo
// visibly contrasts with the medium length used in Scene 4.
const INSIGHT_BODY_V5 =
  "John's prologue echoes Genesis 1 — but here the darkness doesn't get the last word. The Greek katélaben means both 'overtake' and 'comprehend.' The darkness cannot grasp Him in either sense."

// V1 insight — MEDIUM. ~225 words target in the spec; here ~95 words
// so the visitor sees two distinct paragraph blocks in the card.
const INSIGHT_BODY_V1 =
  "The Greek logos was the densest theological term in John's world. To Greeks it meant the rational principle that holds the cosmos together — to Jews it echoed the creating word of Genesis 1 and the personified wisdom of Proverbs 8. John collapses both traditions into one stunning claim: this Word, this logos, became a person.\n\nNotice the chiastic structure: the Word was with God, and the Word was God. The same verb 'was' (ἦν, imperfect of εἰμί) appears three times — eternal continuous existence, not coming-into-being. Verses 14 and 18 are where the prologue finally lands the incarnation."

// Greek interlinear for John 1:1 — surface form + simple gloss, matching
// the two-line stacked pattern in VerseAccordion (original Greek above,
// English gloss below).
const GREEK_V1 = [
  { surface: 'Ἐν ἀρχῇ', gloss: 'In beginning' },
  { surface: 'ἦν', gloss: 'was' },
  { surface: 'ὁ λόγος', gloss: 'the Word' },
  { surface: 'καὶ', gloss: 'and' },
  { surface: 'ὁ λόγος', gloss: 'the Word' },
  { surface: 'ἦν πρὸς', gloss: 'was with' },
  { surface: 'τὸν θεόν', gloss: 'God' },
]

// Word-popover content for the click target in Scene 4. Mirrors what
// the real WordPopover renders (lemma + transliteration + gloss +
// definition + in-verse morphology + Go Deeper teaser). Targeting
// ὁ λόγος at GREEK_V1 index 2 since logos is the most theologically
// freighted word in the verse.
const LOGOS_POPOVER = {
  wordIndex: 2,
  lemma: 'λόγος',
  transliteration: 'logos',
  gloss: 'word',
  definition:
    'A word, message, or statement — but in Greek philosophy logos meant far more: the rational principle that orders the cosmos, the divine reason behind all things. John takes the densest philosophical term of his world and applies it to Jesus.',
  morphology: 'Nominative singular masculine — the grammatical subject of the clause.',
  // Go Deeper content — semantic range, etymology, usage notes. The real
  // popover gates this behind Premium; the demo shows it unlocked because
  // the landing page is selling that exact tier.
  goDeeper: {
    range:
      'Semantic range spans "word/saying" → "discourse/account" → philosophical "reason / ordering principle." In Stoic thought, the logos was the divine fire that orders the universe; in Philo it was a personified mediator between God and creation.',
    etymology:
      'From legō ("to gather, lay, choose") — implies deliberate arrangement, not random speech. The word carries the sense of something said with intention.',
    usage:
      'John uses logos 36 times in his writings; most theologically loaded in the prologue (John 1:1, 1:14), where it becomes a name for the pre-incarnate Christ.',
  },
}

// Single source of truth for the cycle timeline. Each entry runs at its
// absolute offset from cycle start; numbers are tuned by feel — bump
// holds to let a beat breathe, trim them to make the cycle snappier.
function buildTimeline(setters) {
  const {
    setSelected,
    setHighlightPickerOpen,
    addHighlight,
    setNoteEditorOpen,
    setNoteDraft,
    saveNote,
    setInsightVerse,
    setInsightText,
    setInsightDone,
    setInsightLength,
    setOriginalLanguageVerse,
    setActiveWordIdx,
    setPopoverOpen,
    setCursor,
    registerInterval,
    reset,
    finish,
  } = setters

  const events = []
  let t = 0
  const at = (delay, fn) => {
    t += delay
    events.push({ at: t, fn })
  }

  // ── Scene 1: highlight v1 pink (rose), v4 blue ─────────────────────
  // Click rhythm for every interaction:
  //   HOVER (400ms) → CLICK true (350ms ring pulses) → ACTION
  // The 350ms hold between click=true and the action lets the gold ring
  // burst become visible BEFORE the modal / state change fires. Without
  // that hold, clicks looked like the cursor was just hovering past.
  at(600, () => setCursor({ target: 'verse-1', click: false }))
  at(450, () => setCursor({ target: 'verse-1', click: true }))
  at(400, () => setSelected(1))
  at(550, () => setCursor({ target: 'pill-highlight', click: false }))
  at(400, () => setCursor({ target: 'pill-highlight', click: true }))
  at(400, () => setHighlightPickerOpen(true))
  at(500, () => setCursor({ target: 'picker-pink', click: false }))
  at(400, () => setCursor({ target: 'picker-pink', click: true }))
  at(450, () => {
    addHighlight(1, HC.pink)
    setHighlightPickerOpen(false)
    setSelected(null)
  })
  at(600, () => setCursor({ target: 'verse-4', click: false }))
  at(400, () => setCursor({ target: 'verse-4', click: true }))
  at(400, () => setSelected(4))
  at(550, () => setCursor({ target: 'pill-highlight', click: false }))
  at(400, () => setCursor({ target: 'pill-highlight', click: true }))
  at(400, () => setHighlightPickerOpen(true))
  at(500, () => setCursor({ target: 'picker-blue', click: false }))
  at(400, () => setCursor({ target: 'picker-blue', click: true }))
  at(450, () => {
    addHighlight(4, HC.blue)
    setHighlightPickerOpen(false)
    setSelected(null)
  })

  // ── Scene 2: tap v3, type note, save ───────────────────────────────
  at(600, () => setCursor({ target: 'verse-3', click: false }))
  at(400, () => setCursor({ target: 'verse-3', click: true }))
  at(400, () => setSelected(3))
  at(550, () => setCursor({ target: 'pill-note', click: false }))
  at(400, () => setCursor({ target: 'pill-note', click: true }))
  at(400, () => setNoteEditorOpen(true))
  // Type the note one char at a time on a 35ms tick. ~2.3s for ~65 chars.
  at(700, () => {
    let i = 0
    const id = setInterval(() => {
      i += 1
      setNoteDraft(NOTE_TEXT.slice(0, i))
      if (i >= NOTE_TEXT.length) clearInterval(id)
    }, 35)
    registerInterval(id)
  })
  at(NOTE_TEXT.length * 35 + 300, () => setCursor({ target: 'note-save', click: false }))
  at(400, () => setCursor({ target: 'note-save', click: true }))
  at(450, () => {
    saveNote(3, NOTE_TEXT)
    setNoteEditorOpen(false)
    setNoteDraft('')
    setSelected(null)
  })

  // ── Scene 3: AI Insight on v5 — SHORT length ───────────────────────
  at(600, () => setCursor({ target: 'verse-5', click: false }))
  at(400, () => setCursor({ target: 'verse-5', click: true }))
  at(400, () => setSelected(5))
  at(550, () => setCursor({ target: 'pill-insight', click: false }))
  at(400, () => setCursor({ target: 'pill-insight', click: true }))
  at(450, () => {
    setInsightLength('short')
    setInsightVerse(5)
  })
  // Stream the body. ~22 chars/sec — fast enough to keep the loop moving.
  at(600, () => {
    let i = 0
    const id = setInterval(() => {
      i += 2
      setInsightText(INSIGHT_BODY_V5.slice(0, i))
      if (i >= INSIGHT_BODY_V5.length) {
        clearInterval(id)
        setInsightDone(true)
      }
    }, 22)
    registerInterval(id)
  })
  at(Math.ceil(INSIGHT_BODY_V5.length / 2) * 22 + 1800, () => setSelected(null))

  // ── Scene 4: AI Insight on v1 (MEDIUM length), close, Greek + word click
  // Show the medium-length insight first, let it finish streaming, hold
  // a beat so the reader can absorb the two paragraphs, then slide the
  // sheet back down. AFTER the sheet is gone, the Original Language
  // accordion expands; THEN we light up the λόγος word and pop its
  // dictionary popover so visitors see what tapping a Greek word does.
  at(600, () => setCursor({ target: 'verse-1', click: false }))
  at(400, () => setCursor({ target: 'verse-1', click: true }))
  at(400, () => setSelected(1))
  at(550, () => setCursor({ target: 'pill-insight', click: false }))
  at(400, () => setCursor({ target: 'pill-insight', click: true }))
  at(450, () => {
    setInsightLength('medium')
    setInsightVerse(1)
    setInsightText('')
    setInsightDone(false)
    setCursor(null)
  })
  at(500, () => {
    let i = 0
    const id = setInterval(() => {
      i += 2
      setInsightText(INSIGHT_BODY_V1.slice(0, i))
      if (i >= INSIGHT_BODY_V1.length) {
        clearInterval(id)
        setInsightDone(true)
      }
    }, 22)
    registerInterval(id)
  })
  // Insight stream finishes + hold to read, then close the sheet.
  at(Math.ceil(INSIGHT_BODY_V1.length / 2) * 22 + 2200, () => {
    setInsightVerse(null)
    setInsightText('')
    setInsightDone(false)
    setSelected(null)
  })
  // Wait for the sheet's 360ms slide-down animation to clear, then
  // open the Greek accordion under v1.
  at(700, () => setOriginalLanguageVerse(1))
  // Cursor moves to λόγος (anchored via data-cursor-target), pulses,
  // then the word lights up and the popover slides in beneath it.
  at(900, () => setCursor({ target: 'greek-logos', click: false }))
  at(400, () => setCursor({ target: 'greek-logos', click: true }))
  at(400, () => setActiveWordIdx(LOGOS_POPOVER.wordIndex))
  at(350, () => setPopoverOpen(true))

  // ── Hold the popover, then reset and loop ─────────────────────────
  at(5000, () => reset())
  at(400, () => finish())

  return events
}

function VerseSpan({ verse, isSelected, highlightColor, hasNote, onClick }) {
  // Tight span: <sup> + text only. Inter-verse space lives in the parent
  // (see chapter render below). This keeps the highlight background from
  // bleeding backward into the previous verse's trailing space.
  return (
    <span
      role="button"
      tabIndex={-1}
      onClick={() => onClick(verse.num)}
      data-cursor-target={`verse-${verse.num}`}
      className="inline cursor-pointer rounded-sm transition-colors"
      style={{
        backgroundColor: isSelected
          ? 'rgba(233,195,73,0.25)'
          : highlightColor
            ? `${highlightColor}30`
            : undefined,
        borderBottom: isSelected
          ? '2px solid #e9c349'
          : highlightColor
            ? `2px solid ${highlightColor}`
            : undefined,
      }}
    >
      <sup className="text-[0.65em] font-bold mr-0.5 select-none" style={{ color: '#e9c349' }}>
        {verse.num}
      </sup>
      {verse.text}
      {hasNote && (
        <StickyNote
          className="inline-block w-3 h-3 ml-1 -mt-1 opacity-70"
          style={{ color: '#e9c349' }}
          aria-label="Has note"
        />
      )}
    </span>
  )
}

function ActionPill({ visible }) {
  // Floating action toolbar — visual mock of BibleVerseActions. Pure
  // display, no handlers — the scripted timeline drives the next step.
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 320 }}
          className="absolute left-1/2 -translate-x-1/2 bottom-4 flex items-center gap-1 px-3 py-2 rounded-2xl shadow-2xl"
          style={{
            background: '#1a1f3a',
            border: '1px solid rgba(233,195,73,0.18)',
            zIndex: 10,
          }}
        >
          <span className="text-[11px] font-medium mr-1" style={{ color: '#8891b0' }}>
            1 verse
          </span>
          {[
            { Icon: Highlighter, key: 'pill-highlight' },
            { Icon: StickyNote, key: 'pill-note' },
            { Icon: BookOpen, key: 'pill-insight' },
            { Icon: Languages, key: 'pill-languages' },
          ].map(({ Icon, key }) => (
            <div
              key={key}
              data-cursor-target={key}
              className="p-1.5 rounded-xl"
              style={{ color: '#8891b0' }}
            >
              <Icon className="w-4 h-4" aria-hidden="true" />
            </div>
          ))}
          <div className="w-px h-5 mx-0.5" style={{ background: 'rgba(255,255,255,0.08)' }} />
          <div className="p-1.5 rounded-xl" style={{ color: '#8891b0' }}>
            <X className="w-4 h-4" aria-hidden="true" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function HighlightPicker({ open }) {
  // Inline overlay — mirror of BibleHighlightPicker modal layout
  // (5 color swatches + remove). Demo skips the remove button so the
  // visual reads as "pick a color" only.
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.94 }}
          transition={{ duration: 0.18 }}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl p-4 w-60 shadow-2xl"
          style={{
            background: '#1a1f3a',
            border: '1px solid rgba(233,195,73,0.18)',
            zIndex: 20,
          }}
        >
          <p className="text-xs font-medium mb-3" style={{ color: '#dce1ff' }}>
            Highlight Color
          </p>
          <div className="flex items-center justify-between gap-2">
            {Object.entries(HC).map(([id, color]) => (
              <span
                key={id}
                data-cursor-target={`picker-${id}`}
                className="w-8 h-8 rounded-full border-2 border-transparent"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function NoteEditor({ open, draft }) {
  // Inline overlay mirroring BibleNoteEditor — title bar with reference,
  // textarea with the typing draft, Cancel / Save row. Save button glows
  // gold so it reads as the active call to action.
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.94 }}
          transition={{ duration: 0.18 }}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl p-4 w-72 shadow-2xl"
          style={{
            background: '#1a1f3a',
            border: '1px solid rgba(233,195,73,0.18)',
            zIndex: 20,
          }}
        >
          <p className="text-xs font-medium mb-3" style={{ color: '#dce1ff' }}>
            Note — John 1:3
          </p>
          <div
            className="rounded-xl px-3 py-2 text-[12px] leading-relaxed min-h-[68px]"
            style={{
              background: '#0b1229',
              border: '1px solid #e9c349',
              color: '#dce1ff',
            }}
          >
            {draft}
            <span
              aria-hidden="true"
              className="inline-block w-[1px] ml-0.5 animate-pulse"
              style={{
                backgroundColor: 'currentColor',
                height: '1em',
                verticalAlign: 'text-bottom',
              }}
            />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <span className="px-3 py-1.5 rounded-lg text-[11px]" style={{ color: '#8891b0' }}>
              Cancel
            </span>
            <span
              data-cursor-target="note-save"
              className="px-3 py-1.5 rounded-lg text-[11px] font-semibold"
              style={{ background: '#e9c349', color: '#3c2f00' }}
            >
              Save
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function InsightPanel({ verse, text, done, activeLength = 'long' }) {
  // Bottom-sheet insight panel — visual mirror of the real
  // BibleInsightPanel mobile sheet at <md breakpoint. Slides up from the
  // bottom of the demo card so the chapter stays in view above. Pure
  // display, no real interactions; the timeline drives state.
  //
  // Structure from top to bottom (matches the actual app):
  //   1) Header row — BookOpen + verse reference + X close
  //   2) Length pills — Short / Medium / Long (Long pre-selected here)
  //   3) Lens chip row — 6 chips, wraps to two rows
  //   4) Insight card — Sparkles + lens label + "Save to Notes" button,
  //      then the streaming markdown body
  const LENS_CHIPS = [
    { id: 'quick', label: 'Quick summary' },
    { id: 'historical', label: 'Historical' },
    { id: 'crossref', label: 'Cross-references' },
    { id: 'theology', label: 'Theology' },
    { id: 'application', label: 'Application' },
    { id: 'custom', label: 'Ask your own' },
  ]
  const LENGTHS = [
    { id: 'short', label: 'Short' },
    { id: 'medium', label: 'Medium' },
    { id: 'long', label: 'Long' },
  ]
  const ACTIVE_LENGTH = activeLength

  return (
    <AnimatePresence>
      {verse !== null && (
        <motion.aside
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ duration: 0.36, ease: [0.16, 1, 0.3, 1] }}
          className="absolute left-0 right-0 bottom-0 flex flex-col overflow-hidden"
          style={{
            height: '60%',
            background: '#0e1426',
            borderTop: '1px solid rgba(233,195,73,0.18)',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            boxShadow: '0 -12px 32px rgba(0,0,0,0.45)',
            zIndex: 15,
          }}
        >
          {/* Drag handle — small grey pill, visual cue that this is a sheet */}
          <div className="flex justify-center py-1.5 shrink-0">
            <div
              className="w-10 h-1 rounded-full"
              style={{ background: 'rgba(255,255,255,0.18)' }}
            />
          </div>

          {/* 1) Header row */}
          <div className="flex items-center justify-between px-4 pb-2.5 pt-1 shrink-0">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" style={{ color: '#e9c349' }} aria-hidden="true" />
              <p className="text-[13px] font-semibold" style={{ color: '#dce1ff' }}>
                John 1:{verse}
              </p>
            </div>
            <X className="w-4 h-4" style={{ color: '#8891b0' }} aria-hidden="true" />
          </div>

          {/* 2) Length pills */}
          <div className="flex items-center gap-2 px-4 py-2 shrink-0">
            <span className="text-[11px] font-medium" style={{ color: '#8891b0' }}>
              Length:
            </span>
            <div className="flex gap-1.5">
              {LENGTHS.map(l => {
                const active = l.id === ACTIVE_LENGTH
                return (
                  <span
                    key={l.id}
                    className="px-2.5 py-1 rounded-full text-[11px] font-medium"
                    style={{
                      background: active ? '#e9c349' : 'transparent',
                      color: active ? '#3c2f00' : '#dce1ff',
                      border: `1px solid ${active ? '#e9c349' : 'rgba(255,255,255,0.12)'}`,
                    }}
                  >
                    {l.label}
                  </span>
                )
              })}
            </div>
          </div>

          {/* 3) Lens chip row — wraps to two rows on narrow widths */}
          <div className="flex flex-wrap gap-1.5 px-4 pt-1 pb-3 shrink-0">
            {LENS_CHIPS.map(c => (
              <span
                key={c.id}
                className="px-2.5 py-1 rounded-full text-[11px] font-medium"
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.14)',
                  color: '#dce1ff',
                }}
              >
                {c.label}
              </span>
            ))}
          </div>

          {/* 4) Insight card — wraps the streaming response */}
          <div className="flex-1 overflow-hidden px-4 pb-4">
            <div
              className="h-full flex flex-col rounded-xl overflow-hidden"
              style={{
                background: 'rgba(20,26,50,0.6)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              {/* Card header — lens icon + label + Save to Notes pill */}
              <div className="flex items-center justify-between px-4 pt-3.5 pb-2 shrink-0">
                <div className="flex items-center gap-1.5">
                  <Sparkles
                    className="w-3.5 h-3.5"
                    style={{ color: '#e9c349' }}
                    aria-hidden="true"
                  />
                  <span
                    className="text-[10px] font-semibold uppercase tracking-widest"
                    style={{ color: '#e9c349' }}
                  >
                    Quick Summary
                  </span>
                </div>
                <span
                  className="px-2.5 py-1 rounded-full text-[10px] font-medium"
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.16)',
                    color: '#dce1ff',
                  }}
                >
                  Save to Notes
                </span>
              </div>

              {/* Streaming body — split on `\n\n` so Medium/Long responses
                  render as distinct paragraphs (matching MarkdownMessage's
                  `<p className="mb-2">` in the real chat). Cursor lives in
                  the trailing paragraph until streaming completes. */}
              <div className="flex-1 overflow-y-auto dark-scrollbar px-4 pb-4">
                {text.split('\n\n').map((chunk, i, arr) => (
                  <p
                    key={i}
                    className="text-[13px] leading-relaxed"
                    style={{
                      color: '#dce1ff',
                      marginBottom: i < arr.length - 1 ? '0.9em' : 0,
                    }}
                  >
                    {chunk}
                    {!done && i === arr.length - 1 && (
                      <span
                        aria-hidden="true"
                        className="inline-block w-[1px] ml-0.5 animate-pulse"
                        style={{
                          backgroundColor: '#e9c349',
                          height: '1em',
                          verticalAlign: 'text-bottom',
                        }}
                      />
                    )}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  )
}

function WordPopover({ open }) {
  // Visual mirror of OriginalLanguage/WordPopover.jsx. Positions as a
  // block element BELOW the interlinear words wrapper — width is fixed
  // (vs. the real popover's portal + viewport-clamped width) so it sits
  // squarely under the highlighted word and never inherits a tiny
  // "90% of a 60px word" width.
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 6, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 4, scale: 0.97 }}
          transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
          className="mt-3 mx-auto rounded-2xl p-4"
          style={{
            width: '100%',
            maxWidth: 360,
            background: '#141a32',
            border: '1px solid rgba(233,195,73,0.4)',
            boxShadow: '0 18px 40px rgba(0,0,0,0.55)',
          }}
        >
          {/* Header row — lemma + close */}
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="min-w-0 flex-1">
              <div
                className="text-2xl font-semibold leading-tight"
                style={{
                  color: '#dce1ff',
                  fontFamily: "'SBL Greek', 'New Athena Unicode', serif",
                }}
              >
                {LOGOS_POPOVER.lemma}
              </div>
              <div className="text-xs mt-1" style={{ color: '#8891b0' }}>
                <span>{LOGOS_POPOVER.transliteration}</span>
                <span> · </span>
                <span>"{LOGOS_POPOVER.gloss}"</span>
              </div>
            </div>
            <X className="w-4 h-4 shrink-0 mt-1" style={{ color: '#8891b0' }} aria-hidden="true" />
          </div>

          {/* Definition */}
          <p
            className="text-[13px] leading-relaxed"
            style={{ color: '#dce1ff', textAlign: 'left' }}
          >
            {LOGOS_POPOVER.definition}
          </p>

          {/* Morphology / "In this verse" */}
          <p
            className="mt-2.5 text-[11.5px] leading-relaxed"
            style={{ color: '#8891b0', textAlign: 'left' }}
          >
            <span className="font-semibold" style={{ color: '#a8b0d4' }}>
              In this verse:
            </span>{' '}
            {LOGOS_POPOVER.morphology}
          </p>

          {/* Go Deeper — premium unlocked. Inline expanded content
              (Range / Etymology / Usage) shown as if the viewer already
              has the tier, so the landing-page demo sells what they're
              about to get. */}
          {/* Go Deeper — collapsed by default. Premium-unlocked (no lock
              icon, no "Premium" badge), but the demo doesn't open it —
              just shows the affordance so visitors see it's available. */}
          <div
            className="mt-3 pt-3 flex items-center gap-1.5 text-[11px] font-semibold"
            style={{ borderTop: '1px solid rgba(255,255,255,0.08)', color: '#e9c349' }}
          >
            <span>▸</span>
            <span>Go Deeper — semantic range, etymology, usage notes</span>
          </div>

          {/* Ask AI — premium handoff button shown unlocked. In the real
              app this pre-fills the chat composer with the verse + lemma
              + definition; the demo just shows the affordance. */}
          <div
            className="mt-3 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold"
            style={{
              background: 'rgba(233,195,73,0.12)',
              color: '#e9c349',
            }}
          >
            <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
            Ask AI about this word
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function OriginalLanguageAccordion({ visible, activeWordIdx, popoverOpen }) {
  // Inline accordion — mirror of VerseAccordion's two-line stacked
  // interlinear layout. Greek surface on top (bigger serif), English
  // gloss underneath, both flow LTR. Renders inline beneath v1.
  return (
    <AnimatePresence>
      {visible && (
        <motion.span
          initial={{ opacity: 0, scaleY: 0.5 }}
          animate={{ opacity: 1, scaleY: 1 }}
          exit={{ opacity: 0, scaleY: 0.5 }}
          transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
          className="block my-3 ml-1 rounded-xl px-4 py-3"
          style={{
            background: 'rgba(20,26,50,0.6)',
            border: '1px solid rgba(233,195,73,0.2)',
            transformOrigin: 'top',
          }}
        >
          <span className="flex items-center gap-2 mb-3">
            <Languages className="w-3.5 h-3.5" style={{ color: '#e9c349' }} aria-hidden="true" />
            <span
              className="text-[11px] font-medium uppercase tracking-wider"
              style={{ color: '#8891b0' }}
            >
              Original Language · John 1:1
            </span>
          </span>
          <span className="block">
            <span className="flex flex-wrap gap-x-4 gap-y-2">
              {GREEK_V1.map((w, i) => {
                const isActive = activeWordIdx === i
                return (
                  <span
                    key={i}
                    data-cursor-target={i === LOGOS_POPOVER.wordIndex ? 'greek-logos' : undefined}
                    className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-md"
                    style={{
                      background: isActive ? 'rgba(233,195,73,0.18)' : 'transparent',
                      outline: isActive ? '1px solid rgba(233,195,73,0.5)' : 'none',
                      transition: 'background 220ms ease',
                    }}
                  >
                    <span
                      lang="el"
                      className="leading-tight text-lg"
                      style={{
                        color: '#dce1ff',
                        fontFamily: "'SBL Greek', 'New Athena Unicode', serif",
                      }}
                    >
                      {w.surface}
                    </span>
                    <span className="text-[10px]" style={{ color: '#8891b0' }}>
                      {w.gloss}
                    </span>
                  </span>
                )
              })}
            </span>
            {/* Single popover sibling — sits under the words wrapper with
                a proper width, instead of inheriting a tiny per-word width. */}
            <span className="block">
              <WordPopover open={popoverOpen} />
            </span>
          </span>
        </motion.span>
      )}
    </AnimatePresence>
  )
}

export default function BibleReaderDemo() {
  const containerRef = useRef(null)
  const [cycleId, setCycleId] = useState(0)
  const [selected, setSelected] = useState(null)
  const [highlights, setHighlights] = useState(() => new Map())
  const [notes, setNotes] = useState(() => new Set())
  const [highlightPickerOpen, setHighlightPickerOpen] = useState(false)
  const [noteEditorOpen, setNoteEditorOpen] = useState(false)
  const [noteDraft, setNoteDraft] = useState('')
  const [insightVerse, setInsightVerse] = useState(null)
  const [insightText, setInsightText] = useState('')
  const [insightDone, setInsightDone] = useState(false)
  const [insightLength, setInsightLength] = useState('short')
  const [originalLanguageVerse, setOriginalLanguageVerse] = useState(null)
  const [activeWordIdx, setActiveWordIdx] = useState(null)
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [cursor, setCursor] = useState(null)

  // Intervals (note-typing tick, insight stream tick) are started by
  // timeline events at runtime, so we collect their ids here. Cleanup
  // clears both timeouts and intervals on cycle reset / unmount.
  const intervalsRef = useRef([])

  useEffect(() => {
    intervalsRef.current = []

    const setters = {
      setSelected,
      setHighlightPickerOpen,
      addHighlight: (v, c) =>
        setHighlights(prev => {
          const next = new Map(prev)
          next.set(v, c)
          return next
        }),
      setNoteEditorOpen,
      setNoteDraft,
      saveNote: v =>
        setNotes(prev => {
          const next = new Set(prev)
          next.add(v)
          return next
        }),
      setInsightVerse,
      setInsightText,
      setInsightDone,
      setInsightLength,
      setOriginalLanguageVerse,
      setActiveWordIdx,
      setPopoverOpen,
      setCursor,
      registerInterval: id => {
        intervalsRef.current.push(id)
      },
      reset: () => {
        setSelected(null)
        setHighlights(new Map())
        setNotes(new Set())
        setHighlightPickerOpen(false)
        setNoteEditorOpen(false)
        setNoteDraft('')
        setInsightVerse(null)
        setInsightText('')
        setInsightDone(false)
        setInsightLength('short')
        setOriginalLanguageVerse(null)
        setActiveWordIdx(null)
        setPopoverOpen(false)
        setCursor(null)
      },
      finish: () => setCycleId(c => c + 1),
    }

    const events = buildTimeline(setters)
    const timers = events.map(e => setTimeout(e.fn, e.at))

    return () => {
      timers.forEach(clearTimeout)
      intervalsRef.current.forEach(clearInterval)
      intervalsRef.current = []
    }
  }, [cycleId])

  const insightOpen = insightVerse !== null

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden rounded-2xl"
      style={{
        backgroundColor: '#0b1229',
        border: '1px solid rgba(233,195,73,0.15)',
        // Bento-friendly height (matches Chat's 620) so the demo doesn't
        // grow the grid rows oversized when embedded in the landing page.
        // Content scrolls within as the OL accordion / insight sheet
        // rises.
        height: 620,
      }}
    >
      {/* Soft gold radial wash */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 40% 0%, rgba(233,195,73,0.05) 0%, transparent 70%)',
        }}
      />

      {/* Chapter content — sits above the bottom-sheet insight panel.
          Padding is asymmetric: extra space at the bottom so verses
          don't sit flush against where the sheet rises from. */}
      <div className="relative overflow-hidden">
        <div className="px-6 md:px-10 pt-8 pb-6 md:pt-10 md:pb-8 max-w-2xl mx-auto">
          <p
            className="text-center text-[10px] uppercase tracking-[0.3em] mb-6"
            style={{ color: '#8891b0' }}
          >
            John · NIV
          </p>

          <p
            className="leading-[1.9]"
            style={{
              fontFamily: "'Noto Serif', Georgia, serif",
              fontSize: '15.5px',
              color: '#dce1ff',
            }}
          >
            <span
              className="float-left font-bold select-none mr-3 text-[3em]"
              style={{
                fontFamily: "'Noto Serif', Georgia, serif",
                color: '#e9c349',
                lineHeight: '0.85',
                marginTop: '0.05em',
              }}
              aria-hidden="true"
            >
              1
            </span>

            {/* Verses — Fragment + trailing space lives BETWEEN spans, not
                inside them, so highlight backgrounds stop at the verse
                text edge. */}
            {VERSES.map((v, i) => (
              <Fragment key={v.num}>
                <VerseSpan
                  verse={v}
                  isSelected={selected === v.num}
                  highlightColor={highlights.get(v.num)}
                  hasNote={notes.has(v.num)}
                  onClick={() => {}}
                />
                {originalLanguageVerse === v.num && (
                  <OriginalLanguageAccordion
                    visible={true}
                    activeWordIdx={activeWordIdx}
                    popoverOpen={popoverOpen}
                  />
                )}
                {i < VERSES.length - 1 ? ' ' : null}
              </Fragment>
            ))}
          </p>
        </div>
      </div>

      {/* Action pill — hides when the insight sheet is up so they don't
          fight for the bottom of the frame. */}
      <ActionPill
        visible={selected !== null && !highlightPickerOpen && !noteEditorOpen && !insightOpen}
      />
      <HighlightPicker open={highlightPickerOpen} />
      <NoteEditor open={noteEditorOpen} draft={noteDraft} />

      {/* Insight panel — bottom sheet that slides up from below. */}
      <InsightPanel
        verse={insightVerse}
        text={insightText}
        done={insightDone}
        activeLength={insightLength}
      />

      {/* Virtual cursor — last in render order so it sits above
          everything (action pill, picker, sheet, word popover). */}
      <DemoCursor cursor={cursor} containerRef={containerRef} />
    </div>
  )
}
